// Ticket form component
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import {
  ticketSchema,
  type TicketInput,
  ticketStatuses,
  ticketPriorities,
  ticketCategories,
  ticketTypes,
  ticketChannels,
  type TicketCategory,
} from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { OwnerSelect } from './OwnerSelect'
import { apiRequest } from '../../lib/api'

interface Props {
  defaultValues?: Partial<TicketInput>
  onSubmit: (data: TicketInput) => void
  onCancel?: () => void
  loading?: boolean
  fixedCategory?: TicketCategory
}

export function TicketForm({ defaultValues, onSubmit, onCancel, loading, fixedCategory }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<TicketInput>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      status: 'open',
      priority: 'medium',
      category: fixedCategory || 'support',
      ...defaultValues,
    },
  })

  const category = watch('category')
  const isSupport = category === 'support'

  // Fetch accounts for selector
  const { data: accountsResponse } = useQuery({
    queryKey: ['accounts', 'list'],
    queryFn: () => apiRequest<{ data: { id: string; name: string }[] }>('/accounts?limit=100'),
    enabled: isSupport,
  })

  // Fetch contacts for selector
  const { data: contactsResponse } = useQuery({
    queryKey: ['contacts', 'list'],
    queryFn: () => apiRequest<{ data: { id: string; firstName: string | null; lastName: string; accountId: string }[] }>('/contacts?limit=100'),
    enabled: isSupport,
  })

  const accounts = accountsResponse?.data || []
  const contacts = contactsResponse?.data || []

  // Filter types based on category
  const getTypeOptions = () => {
    if (category === 'development') {
      return ['bug', 'feature_request', 'task'] as const
    }
    return ['question', 'problem', 'incident', 'feature_request'] as const
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Category selector - hidden if fixed */}
      {!fixedCategory && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('fields.category')} <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 flex gap-2">
            {ticketCategories.map((cat) => (
              <label key={cat} className="flex items-center">
                <input
                  type="radio"
                  {...register('category')}
                  value={cat}
                  className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {t(`pages.tickets.categories.${cat}` as any)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Subject */}
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
          {t('fields.subject')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('subject')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder={t('pages.tickets.subjectPlaceholder' as any)}
        />
        {errors.subject && (
          <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          {t('fields.description')} <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register('description')}
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder={t('pages.tickets.descriptionPlaceholder' as any)}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Priority */}
        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
            {t('fields.priority')}
          </label>
          <select
            {...register('priority')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            {ticketPriorities.map((priority) => (
              <option key={priority} value={priority}>
                {t(`pages.tickets.priorities.${priority}` as any)}
              </option>
            ))}
          </select>
        </div>

        {/* Type */}
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700">
            {t('fields.type')}
          </label>
          <select
            {...register('type')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            <option value="">{t('common.select')}</option>
            {getTypeOptions().map((type) => (
              <option key={type} value={type}>
                {t(`pages.tickets.types.${type}` as any)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Support-specific fields */}
      {isSupport && (
        <div className="grid grid-cols-2 gap-4">
          {/* Account */}
          <div>
            <label htmlFor="accountId" className="block text-sm font-medium text-gray-700">
              {t('fields.account')}
            </label>
            <select
              {...register('accountId')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="">{t('common.select')}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>

          {/* Contact */}
          <div>
            <label htmlFor="contactId" className="block text-sm font-medium text-gray-700">
              {t('fields.contact')}
            </label>
            <select
              {...register('contactId')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="">{t('common.select')}</option>
              {contacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.firstName} {contact.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Assigned To */}
        <Controller
          name="assignedToId"
          control={control}
          render={({ field }) => (
            <OwnerSelect
              value={field.value || undefined}
              onChange={field.onChange}
              disabled={loading}
              label={t('fields.assignedTo')}
            />
          )}
        />

        {/* Due Date */}
        <div>
          <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
            {t('fields.dueDate')}
          </label>
          <input
            {...register('dueDate')}
            type="datetime-local"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Status - only show for editing */}
      {defaultValues?.status && (
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            {t('fields.status')}
          </label>
          <select
            {...register('status')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            {ticketStatuses.map((status) => (
              <option key={status} value={status}>
                {t(`pages.tickets.statuses.${status}` as any)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
